import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Printer } from 'lucide-react';

const PrintCustomerReport = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get data from localStorage
    const savedData = localStorage.getItem('printCustomerReport');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setReportData(parsed);
      } catch (e) {
        console.error('Error parsing report data:', e);
      }
    }
    setLoading(false);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₪${parseFloat(amount || 0).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <p className="text-xl">טוען...</p>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" dir="rtl">
        <p className="text-xl text-red-600">לא נמצאו נתונים</p>
        <Button onClick={() => navigate('/customer-report')}>
          <ArrowRight className="ml-2 h-4 w-4" />
          חזרה לדוח לקוח
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-gray-100 p-4 flex justify-between items-center sticky top-0 z-10 shadow">
        <Button variant="outline" onClick={() => navigate('/customer-report')}>
          <ArrowRight className="ml-2 h-4 w-4" />
          חזרה
        </Button>
        <h2 className="text-lg font-bold">תצוגה מקדימה להדפסה</h2>
        <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
          <Printer className="ml-2 h-4 w-4" />
          הדפס עכשיו
        </Button>
      </div>

      {/* Printable Content */}
      <div className="p-10 max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl font-bold text-center text-blue-600 mb-2">
          דוח ללקוח
        </h1>
        <p className="text-center text-gray-500 mb-6">
          תאריך הדפסה: {new Date().toLocaleDateString('he-IL')}
        </p>

        {/* Customer Info */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
          <div className="grid grid-cols-2 gap-4">
            <p><span className="font-bold">לקוח:</span> {reportData.customerName}</p>
            <p><span className="font-bold">תקופה:</span> {
              reportData.dateRange === 1 ? 'חודש אחרון' :
              reportData.dateRange === 12 ? 'שנה אחרונה' :
              reportData.dateRange === 24 ? 'שנתיים אחרונות' :
              reportData.dateRange === 999 ? 'כל הזמן' :
              `${reportData.dateRange} חודשים אחרונים`
            }</p>
            <p><span className="font-bold">סה"כ רשומות:</span> {reportData.deals?.length || 0}</p>
          </div>
        </div>

        {/* Records */}
        {reportData.deals && reportData.deals.map((deal, dealIndex) => (
          <div key={deal.id || dealIndex} className="mb-8 border-2 border-gray-300 rounded-lg overflow-hidden">
            {/* Record Header */}
            <div className="bg-gray-100 p-4 border-b-2 border-gray-300 flex justify-between items-center">
              <div>
                <p className="font-bold text-lg">
                  {deal.type} #{dealIndex + 1}
                  {deal.projectName && ` - ${deal.projectName}`}
                </p>
                <p className="text-gray-600">תאריך: {formatDate(deal.date)}</p>
              </div>
              <p className="text-xl font-bold text-green-600">
                סה"כ: {formatCurrency(deal.dealTotal)}
              </p>
            </div>

            {/* Items Table */}
            <div className="p-4">
              {((deal.items && deal.items.length > 0) || (deal.workItems && deal.workItems.length > 0)) ? (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-500 text-white">
                      <th className="border border-gray-400 p-2 text-right">חלק</th>
                      <th className="border border-gray-400 p-2 text-center">כמות</th>
                      <th className="border border-gray-400 p-2 text-center">מחיר ליחידה</th>
                      <th className="border border-gray-400 p-2 text-center">מחיר ללקוח</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deal.items && deal.items.map((item, idx) => {
                      const quantity = parseFloat(item.quantity) || 0;
                      const pricePerUnit = parseFloat(item.sellingPrice || item.customerPrice) || 0;
                      const totalPrice = quantity * pricePerUnit;
                      return (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-400 p-2">{item.partName || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">{item.quantity || '0'}</td>
                          <td className="border border-gray-400 p-2 text-center">{formatCurrency(pricePerUnit)}</td>
                          <td className="border border-gray-400 p-2 text-center font-bold">{formatCurrency(item.customerPrice || totalPrice)}</td>
                        </tr>
                      );
                    })}
                    {deal.workItems && deal.workItems.map((item, idx) => {
                      const quantity = parseFloat(item.quantity) || 0;
                      const pricePerUnit = parseFloat(item.transactionAmount) || 0;
                      const totalPrice = quantity * pricePerUnit;
                      return (
                        <tr key={`work-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-400 p-2">{item.partName || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">{item.quantity || '0'}</td>
                          <td className="border border-gray-400 p-2 text-center">{formatCurrency(pricePerUnit)}</td>
                          <td className="border border-gray-400 p-2 text-center font-bold">{formatCurrency(totalPrice)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500 text-center py-4">אין פריטים ברשומה זו</p>
              )}

              {/* Payments */}
              {deal.payments && deal.payments.length > 0 && (
                <div className="mt-4 pt-4 border-t-2 border-gray-200">
                  <h4 className="font-bold mb-2">פרטי תשלום:</h4>
                  {deal.payments.map((payment, pIdx) => (
                    <div key={pIdx} className="flex justify-between bg-blue-50 p-2 rounded mb-1">
                      <span>
                        {payment.method === 'cash' && 'מזומן'}
                        {payment.method === 'credit' && 'כרטיס אשראי'}
                        {payment.method === 'check' && 'שיק'}
                        {payment.method === 'transfer' && 'העברה בנקאית'}
                        {payment.checkNumber && ` - מס׳ ${payment.checkNumber}`}
                      </span>
                      <span className="font-bold text-green-600">{formatCurrency(payment.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Grand Total */}
        <div className="bg-green-100 border-2 border-green-500 rounded-lg p-6 mt-8">
          <div className="flex justify-between items-center">
            <p className="text-2xl font-bold">סה"כ כולל לתקופה:</p>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(reportData.grandTotal)}</p>
          </div>
          <p className="text-gray-600 mt-2">מספר עסקאות: {reportData.deals?.length || 0}</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1cm;
            size: A4;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden,
          .print-hide,
          nav,
          header,
          [data-testid="emergent-badge"],
          iframe {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintCustomerReport;
